import { merge, Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { provideSingleton } from '../inversify';

type ILocalStorageConvertFunction<T> = (value: string) => T;

@provideSingleton(LocalStorageService)
class LocalStorageService {

  /**
   * Emits an event if a key was changed. If null is emitted, the localStorage has been cleared.
   */
  private readonly localStorageChanged: Subject<string | null> = new Subject<string | null>();

  constructor() {
    window.onstorage = (e: StorageEvent) => this.localStorageChanged.next(e.key);
  }

  /**
   * Get a list of all available keys that start with an (optional) [prefix].
   *
   * @param {string} prefix
   * @returns {string[]}
   */
  public keys(prefix: string = ''): string[] {
    const keys = Object.keys(localStorage);
    return keys.filter(k => k.startsWith(prefix));
  }

  /**
   * Store a [value] for a [key].
   *
   * @param {string} key
   * @param {T} value
   */
  public setItem<T>(key: string, value: T) {
    localStorage.setItem(key, JSON.stringify(value));
    this.localStorageChanged.next(key);
  }

  /**
   * Get an item of [key] from the storage with a [defaultValue] that is returned if no entry was found. Use the
   * [convertFunction] to apply additional conversions. By default, `JSON.parse()` is used as convert function.
   * The value is updated when the local storage changes for this key.
   *
   * @param {string} key
   * @param {T} defaultValue
   * @param {ILocalStorageConvertFunction<T>} convertFunction
   * @returns {Observable<T>}
   */
  public getItem<T>(key: string, defaultValue: T, convertFunction?: ILocalStorageConvertFunction<T>): Observable<T> {
    return merge(of(key), this.localStorageChanged.pipe(filter(k => k === null || k === key)))
      .pipe(
        map(() => {
          if (key === null) {
            // Return the default value if the localStorage has been cleared.
            return defaultValue;
          } else {
            const item = localStorage.getItem(key);

            if (item === null) {
              return defaultValue;
            } else if (convertFunction !== undefined) {
              return convertFunction(item);
            } else {
              try {
                const value = JSON.parse(item);
                return typeof value === "number" ? item : value;
              } catch (e) {
                return undefined;
              }
            }
          }
        })
      );
  }

  /**
   * Remove an item of [key] from the storage.
   *
   * @param {string} key
   */
  public removeItem(key: string) {
    localStorage.removeItem(key);
  }
}

export default LocalStorageService;



// WEBPACK FOOTER //
// ./src/api/local_storage_service.ts